import type { TripExpense, TripWorkspace } from "./trip";
export function tripParty(trip: TripWorkspace) {
	return trip.party?.length
		? trip.party
		: Array.from({ length: trip.partySize }, (_, i) => ({
				id: i === 0 ? "me" : `party-${i}`,
				nickname: i === 0 ? "나" : `일행${i}`,
			}));
}
export function expenseJpy(e: TripExpense, rate: number | null) {
	return e.currency === "KRW"
		? rate
			? Math.round(((e.amountKrw ?? 0) * 100) / rate)
			: null
		: e.amountJpy;
}
export function settleExpenses(
	trip: TripWorkspace,
	currency: "JPY" | "KRW" = "JPY",
) {
	const party = tripParty(trip),
		balances = Object.fromEntries(party.map((p) => [p.id, 0])),
		paidTotals = Object.fromEntries(party.map((p) => [p.id, 0])),
		shareTotals = Object.fromEntries(party.map((p) => [p.id, 0]));
	const breakdown: { id: string; title: string; amount: number; paidBy: string; shares: { id: string; amount: number }[] }[] = [];
	let excluded = 0;
	for (const e of trip.expenses) {
		const amount =
			currency === "KRW"
				? e.currency === "KRW"
					? (e.amountKrw ?? 0)
					: null
				: expenseJpy(e, trip.krwPer100Jpy);
		if (amount === null) {
			excluded++;
			continue;
		}
		const paid = party.some((p) => p.id === e.paidBy)
			? e.paidBy!
			: party[0]!.id;
		const selected = (
			e.splitWith?.length ? e.splitWith : party.map((p) => p.id)
		).filter((id) => id in balances);
		if (!selected.length) continue;
		balances[paid]! += amount;
		paidTotals[paid]! += amount;
		const base = Math.floor(amount / selected.length),
			remainder = amount - base * selected.length;
		const remainderId = selected.includes(paid) ? paid : selected[0]!;
		const shares = selected.map((id) => ({ id, amount: base + (id === remainderId ? remainder : 0) }));
		for (const share of shares) { balances[share.id]! -= share.amount; shareTotals[share.id]! += share.amount; }
		breakdown.push({ id: e.id, title: e.title, amount, paidBy: paid, shares });
	}
	const creditors = Object.entries(balances)
			.filter(([, v]) => v > 0)
			.sort((a, b) => b[1] - a[1]),
		debtors = Object.entries(balances)
			.filter(([, v]) => v < 0)
			.map(([id, n]) => [id, -n] as [string, number])
			.sort((a, b) => b[1] - a[1]);
	const rows: { from: string; to: string; amountJpy: number }[] = [];
	let i = 0,
		j = 0;
	while (i < debtors.length && j < creditors.length) {
		const d = debtors[i]!,
			c = creditors[j]!,
			amount = Math.min(d[1], c[1]);
		rows.push({ from: d[0], to: c[0], amountJpy: amount });
		d[1] -= amount;
		c[1] -= amount;
		if (!d[1]) i++;
		if (!c[1]) j++;
	}
	return { balances, rows, excluded, paidTotals, shareTotals, breakdown };
}
export function expensesCsv(trip: TripWorkspace) {
	const cell = (s: unknown) =>
		'"' +
		String(s ?? "")
			.replace(/^[=+@-]/, "'$&")
			.replaceAll('"', '""') +
		'"';
	const party = tripParty(trip);
	return (
		"\ufeff" +
		[
			["날짜", "분류", "내용", "통화", "금액", "낸 사람", "나눌 사람"],
			...trip.expenses.map((e) => [
				e.occurredOn,
				e.category,
				e.title,
				e.currency ?? "JPY",
				e.currency === "KRW" ? e.amountKrw : e.amountJpy,
				party.find((p) => p.id === e.paidBy)?.nickname ?? party[0]!.nickname,
				(e.splitWith ?? party.map((p) => p.id))
					.map((id) => party.find((p) => p.id === id)?.nickname ?? "나")
					.join(" · "),
			]),
		]
			.map((r) => r.map(cell).join(","))
			.join("\r\n")
	);
}
